import { useEffect, useState } from 'react';
import {
  Box, Card, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Button, CircularProgress, Alert, Chip, InputAdornment, Typography,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Grade as GradeIcon, Search as SearchIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const blank = {
  ind: '', na: '', cla: '', te: '', subj: '',
  to_60: 0, cs_50: 0, et_100: 0, es_50: 0, overall: 0, total: 0,
  post: 0, grade: 0, remark: '',
};

export default function ScoresPage() {
  const [rows, setRows]   = useState([]);
  const [classes, setCls] = useState([]);
  const [grades, setGrd]  = useState([]);
  const [loading, setL]   = useState(true);
  const [q, setQ]         = useState('');
  const [cla, setCla]     = useState('');
  const [te, setTe]       = useState('');

  const [open, setOpen]   = useState(false);
  const [editing, setEd]  = useState(null);
  const [form, setForm]   = useState(blank);
  const [saving, setSav]  = useState(false);
  const [error, setErr]   = useState('');

  const load = async () => {
    setL(true);
    try {
      const params = {};
      if (q)   params.q   = q;
      if (cla) params.cla = cla;
      if (te)  params.te  = te;
      setRows((await API.get('/scores', { params })).data);
    } catch (e) { setErr(e.response?.data?.message || 'Load failed'); }
    finally { setL(false); }
  };
  useEffect(() => {
    API.get('/classes').then(r => setCls(r.data)).catch(() => {});
    API.get('/scores/grades').then(r => setGrd(r.data)).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, cla, te]);

  const openNew  = () => { setEd(null); setForm(blank); setErr(''); setOpen(true); };
  const openEdit = (r) => { setEd(r); setForm({ ...blank, ...r }); setErr(''); setOpen(true); };

  // Auto-compute total when component scores change
  const onScoreChange = (field, value) => {
    const next = { ...form, [field]: value };
    next.total = (Number(next.cs_50) || 0) + (Number(next.es_50) || 0);
    next.overall = next.total;
    // Lookup grade
    const g = grades.find(gr => Number(next.total) <= (gr.marks_1 || 0) && Number(next.total) >= (gr.marks_2 || 0));
    if (g) { next.grade = g.grade; next.remark = g.remarks; }
    setForm(next);
  };

  const submit = async () => {
    if (!form.ind || !form.na || !form.subj) { setErr('Student and subject required'); return; }
    setSav(true); setErr('');
    try {
      if (editing) {
        await API.put(`/scores/${editing.trans_code}`, form);
        setOpen(false);
        swal.success('Score updated');
      } else {
        await API.post('/scores', form);
        // Keep class, term, subject for back-to-back score entry
        setForm((f) => ({ ...blank, cla: f.cla, te: f.te, subj: f.subj }));
        swal.success('Score recorded — add another or close');
      }
      await load();
    } catch (e) { setErr(swal.pickError(e, 'Save failed')); }
    finally { setSav(false); }
  };

  const remove = async (r) => {
    const ok = await swal.confirmDelete(`score for ${r.na} — ${r.subj}`);
    if (!ok) return;
    try {
      await API.delete(`/scores/${r.trans_code}`);
      swal.success('Score deleted');
      load();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  return (
    <Box sx={{ maxWidth: 1320, mx: 'auto' }}>
      <ModuleHeader icon={<GradeIcon sx={{ fontSize: 28 }} />}
        title="Examination Scores" subtitle="SBA scores per student, subject and term"
        actionLabel="New Score" actionIcon={<AddIcon />} onAction={openNew} />

      <Card sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField placeholder="Search ID or name…" value={q} onChange={(e) => setQ(e.target.value)}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <TextField select label="Class" value={cla} onChange={(e) => setCla(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">All</MenuItem>
            {classes.map(c => <MenuItem key={c.trans_code} value={c.class}>{c.class}</MenuItem>)}
          </TextField>
          <TextField select label="Term" value={te} onChange={(e) => setTe(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="">All</MenuItem>
            {['Term_1', 'Term_2', 'Term_3'].map(t => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
          </TextField>
        </Box>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{error}</Alert>}

      <Card>
        {loading ? <Box sx={{ p: 8, textAlign: 'center' }}><CircularProgress /></Box> : (
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Student</TableCell><TableCell>Class</TableCell><TableCell>Term</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell align="right">Class Score (50)</TableCell>
                <TableCell align="right">Exam Score (50)</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">Grade</TableCell>
                <TableCell align="right">Position</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.trans_code} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{r.na}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{r.ind}</Typography>
                    </TableCell>
                    <TableCell>{r.cla || '—'}</TableCell>
                    <TableCell>{r.te?.replace('_', ' ') || '—'}</TableCell>
                    <TableCell><strong>{r.subj}</strong></TableCell>
                    <TableCell align="right">{Number(r.cs_50 || 0).toFixed(1)}</TableCell>
                    <TableCell align="right">{Number(r.es_50 || 0).toFixed(1)}</TableCell>
                    <TableCell align="right"><strong>{Number(r.total || 0).toFixed(1)}</strong></TableCell>
                    <TableCell align="center"><Chip size="small" label={r.grade || '—'} color="primary" /></TableCell>
                    <TableCell align="right">{r.post || '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.disabled' }}>No scores</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          color: '#fff',
        }}>
          {editing ? 'Edit Score' : 'New Score'}
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{error}</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <TextField label="Student ID" value={form.ind} onChange={(e) => setForm({ ...form, ind: e.target.value })} required />
            <TextField label="Student Name" value={form.na} onChange={(e) => setForm({ ...form, na: e.target.value })} required />
            <TextField select label="Class" value={form.cla} onChange={(e) => setForm({ ...form, cla: e.target.value })}>
              <MenuItem value="">—</MenuItem>
              {classes.map(c => <MenuItem key={c.trans_code} value={c.class}>{c.class}</MenuItem>)}
            </TextField>
            <TextField select label="Term" value={form.te} onChange={(e) => setForm({ ...form, te: e.target.value })}>
              <MenuItem value="">—</MenuItem>
              {['Term_1', 'Term_2', 'Term_3'].map(t => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
            </TextField>
            <TextField label="Subject" value={form.subj} onChange={(e) => setForm({ ...form, subj: e.target.value })} required sx={{ gridColumn: { sm: 'span 2' } }} />
            <TextField label="Class Score (out of 50)" type="number" value={form.cs_50} onChange={(e) => onScoreChange('cs_50', e.target.value)} />
            <TextField label="Exam Score (out of 50)" type="number" value={form.es_50} onChange={(e) => onScoreChange('es_50', e.target.value)} />
            <TextField label="Total" type="number" value={form.total} disabled />
            <TextField label="Grade" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} />
            <TextField label="Position" type="number" value={form.post} onChange={(e) => setForm({ ...form, post: e.target.value })} />
            <TextField label="Remark" value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
            {editing ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
